import { useState, useEffect } from 'react';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import { useAuth } from '../../context/AuthContext';
import { nomePratica } from '../../utils/pratica';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (n, dec = 2) =>
  n == null || isNaN(Number(n)) ? '—'
  : Number(n).toLocaleString('it-IT', { minimumFractionDigits: dec, maximumFractionDigits: dec, useGrouping: true });

const fmtPct = (n) => n == null || isNaN(n) ? '—' : (n * 100).toFixed(2) + '%';

// ── ICP calculation (same logic as TabRiepilogo) ──────────────────────
function calcolaCPAnno(r, annoReale, inailMap) {
  const base = (r.costo_operai || 0) + (r.costo_tecnici || 0) +
    (r.costo_amministrativi || 0) + (r.costo_dirigenti || 0);
  const numTitolari = r.titolare_soci || 0;
  const inailPerPersona = (numTitolari > 0 && annoReale && inailMap?.[annoReale]) ? inailMap[annoReale] : 0;
  return base + numTitolari * inailPerPersona;
}

function calcolaVARideterminato(VA, Amm, CP) {
  if (!VA || VA <= 0) return VA;
  const rid = [VA];
  if (Amm > 0) { const q = Amm / VA; if (q < 0.02) rid.push(Amm / 0.02); }
  if (CP > 0)  { const p = CP  / VA; if (p < 0.15) rid.push(CP  / 0.15); }
  return Math.min(...rid);
}

function calcolaICPAuto(totVALavori, totVA, totAmm, totCP, nSel, impresa) {
  const baseVA = totVALavori > 0 ? totVALavori : totVA;
  if (!baseVA || baseVA <= 0 || nSel === 0) return null;
  const CA_media  = baseVA / nSel;
  const amm_media = totAmm / nSel;
  const cp_media  = totCP  / nSel;

  const isoOk          = !!impresa?.incremento_iso;
  const patrimonioNetto = impresa?.patrimonio_netto || 0;
  const soglia5  = CA_media * 0.05;
  const soglia10 = CA_media * 0.10;
  const reqA      = patrimonioNetto >= soglia5;
  const reqA_maxi = patrimonioNetto >= soglia10;
  const reqB      = !!impresa?.liquidita_ok;
  const redditiPos = [impresa?.reddito_anno1, impresa?.reddito_anno2, impresa?.reddito_anno3].filter(Boolean).length;
  const reqC      = redditiPos >= 2;
  const reqD_amm  = amm_media >= CA_media * 0.02;
  const reqD_cp   = cp_media  >= CA_media * 0.15;
  const reqD      = reqD_amm && reqD_cp;
  const nReq      = [reqA, reqB, reqC, reqD].filter(Boolean).length;
  const requisiti = { A: reqA, A_maxi: reqA_maxi, B: reqB, C: reqC, D: reqD, nReq, soglia5, soglia10, patrimonioNetto };

  if (!isoOk) return { icp: 0, requisiti, note: 'ISO 9001 non presente' };

  const coeff = reqA_maxi ? 39 : 30;
  const p_cap = Math.min(Math.max(cp_media / CA_media, 0), 0.225);
  const a_cap = Math.min(Math.max(amm_media / CA_media, 0), 0.03);
  const C = nReq >= 3
    ? (coeff / 3) * ((p_cap - 0.15) / 0.075 + (a_cap - 0.02) / 0.01 + 1)
    : 10;
  const icp = Math.max(10, Math.min(C, coeff));
  const note = nReq < 3 ? `${nReq}/4 requisiti — ICP base da ISO` : '';
  return { icp: Math.round(icp * 100) / 100, requisiti, note };
}

function calcolaRiepilogo(dati, inailMap) {
  if (!dati) return null;
  const { cifraAffari: ca, ammortamenti: amm, costoPersonale: cp } = dati;

  const caMain = ca.filter(r => !r.flag_cedente).sort((a, b) => (a.anno || 0) - (b.anno || 0));
  const caByAnno = ca.reduce((acc, r) => {
    const k = r.anno;
    if (!acc[k]) acc[k] = { affari_totale: 0, affari_in_lavori: 0 };
    acc[k].affari_totale    += (r.affari_totale    || 0);
    acc[k].affari_in_lavori += (r.affari_in_lavori || 0);
    return acc;
  }, {});

  function aggAmm(list, pos) {
    return list.filter(r => r.anno_pos === pos).reduce((acc, r) => ({
      amm_attrezzi:     (acc.amm_attrezzi||0)     + (r.amm_attrezzi||0),
      noleggio_stabile: (acc.noleggio_stabile||0)  + (r.noleggio_stabile||0),
      leasing:          (acc.leasing||0)           + (r.leasing||0),
      noleggio:         (acc.noleggio||0)          + (r.noleggio||0),
    }), {});
  }

  const ammMain = amm.filter(r => !r.cedente || r.cedente === 'nessuna');
  const ammCed  = amm.filter(r =>  r.cedente && r.cedente !== 'nessuna');
  const cpMain  = cp.filter(r => !r.cedente || r.cedente === 'nessuna');
  const cpCed   = cp.filter(r =>  r.cedente && r.cedente !== 'nessuna');

  const rows = Array.from({ length: 15 }, (_, i) => {
    const pos = i + 1;
    const caM = caMain[i] || null;
    const caSum = caM?.anno ? (caByAnno[caM.anno] || null) : null;
    const ammR  = aggAmm(ammMain, pos);
    const ammC  = aggAmm(ammCed,  pos);
    const annoReale = caM?.anno || null;
    const cpR = cpMain.filter(r => r.anno_pos === pos).reduce((s, r) => s + calcolaCPAnno(r, annoReale, inailMap), 0);
    const cpC = cpCed.filter(r  => r.anno_pos === pos).reduce((s, r) => s + calcolaCPAnno(r, annoReale, inailMap), 0);

    const va       = caSum?.affari_in_lavori || 0;
    const vaLavori = caSum?.affari_in_lavori || 0;
    const ammNSLF  = (ammR.amm_attrezzi||0) + (ammR.noleggio_stabile||0) + (ammR.leasing||0)
                   + (ammC.amm_attrezzi||0) + (ammC.noleggio_stabile||0) + (ammC.leasing||0);
    const noliOcc  = (ammR.noleggio||0) + (ammC.noleggio||0);
    const totAmm   = ammNSLF + noliOcc;
    const cpTot    = cpR + cpC;
    return { pos, anno: caM?.anno || null, va, vaLavori, ammNSLF, noliOcc, totAmm, cp: cpTot, hasDati: va > 0 || totAmm > 0 || cpTot > 0 };
  });

  return rows;
}

// ── Label iscrizione per il menu a tendina ────────────────────────────
function labelIscrizione(imp) {
  const tipo = imp.richiesta || 'Iscrizione';
  const num  = imp.numero_richiesta ? ` n. ${imp.numero_richiesta}` : '';
  const citta = imp.citta ? ` — ${imp.citta}` : '';
  return `${tipo}${num}${citta}`;
}

// ── TABS ──────────────────────────────────────────────────────────────
const TABS = [
  { key: 'anagrafica',  label: 'Anagrafica' },
  { key: 'lrdt',        label: 'Legali Rapp. e Dir. Tecnici' },
  { key: 'dati',        label: 'Dati economici' },
  { key: 'soa',         label: 'Lavori e Calcolo SOA' },
];

export default function PortaleImpresa() {
  const { utente } = useAuth();
  const impresaAssegnataId = utente?.impresa_id;
  const [tab, setTab] = useState('anagrafica');
  const [gruppo, setGruppo] = useState([]);
  const [impresaSOA, setImpresaSOA] = useState(undefined); // undefined=loading, null=not found
  const [inailMap, setInailMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  // Dati economici
  const [impresaSelId, setImpresaSelId] = useState(null);
  const [datiEconomici, setDatiEconomici] = useState(null);
  const [loadingDati, setLoadingDati] = useState(false);

  // Calcolo SOA
  const [anni, setAnni] = useState(15);
  // Data di riferimento scelta dall'impresa (default oggi, in formato locale YYYY-MM-DD)
  const [dataRif, setDataRif] = useState(() => {
    const o = new Date();
    return `${o.getFullYear()}-${String(o.getMonth() + 1).padStart(2, '0')}-${String(o.getDate()).padStart(2, '0')}`;
  });
  const [calcolo, setCalcolo] = useState(null);
  const [loadingCalcolo, setLoadingCalcolo] = useState(false);
  const [erroreCalcolo, setErroreCalcolo] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get('/api/portale/gruppo'),
      axios.get('/api/portale/soa'),
      axios.get('/api/tabella-inail'),
    ]).then(([gRes, soaRes, inailRes]) => {
      setGruppo(gRes.data);
      setImpresaSOA(soaRes.data); // null if not found
      const map = {};
      (inailRes.data || []).forEach(t => { map[t.anno] = t.importo; });
      setInailMap(map);
      if (gRes.data.length > 0) {
        // La pratica da mostrare è quella assegnata all'utente; solo se manca si ripiega
        // sull'iscrizione con la data contratto più recente.
        const assegnata = gRes.data.find(i => i.id === impresaAssegnataId);
        const ultima = gRes.data.reduce((best, imp) => {
          if (!best.data_contratto) return imp;
          if (!imp.data_contratto) return best;
          return new Date(imp.data_contratto) > new Date(best.data_contratto) ? imp : best;
        }, gRes.data[0]);
        setImpresaSelId((assegnata || ultima).id);
      }
    }).catch(() => setErrore('Errore nel caricamento dati'))
      .finally(() => setLoading(false));
  }, [impresaAssegnataId]);

  useEffect(() => {
    if (!impresaSelId) return;
    setLoadingDati(true);
    setDatiEconomici(null);
    axios.get(`/api/portale/dati-economici/${impresaSelId}`)
      .then(r => setDatiEconomici(r.data))
      .finally(() => setLoadingDati(false));
  }, [impresaSelId]);

  async function eseguiCalcolo() {
    if (!impresaSOA) return;
    if (!dataRif) { setErroreCalcolo('Inserisci la data di riferimento'); return; }
    setLoadingCalcolo(true);
    setErroreCalcolo('');
    try {
      const r = await axios.get(`/api/portale/soa/${impresaSOA.id}/calcolo?anni=${anni}&data_rif=${dataRif}`);
      setCalcolo(r.data);
    } catch (err) {
      setErroreCalcolo(err.response?.data?.error || 'Errore nel calcolo SOA');
    } finally {
      setLoadingCalcolo(false);
    }
  }

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (errore)  return <p className="text-red-600 p-8">{errore}</p>;
  if (gruppo.length === 0) return <p className="text-gray-500 p-8">Nessuna iscrizione trovata per la tua azienda.</p>;

  // Pratica mostrata in anagrafica, LR/DT e dati economici: quella assegnata all'utente
  const impresaSel = gruppo.find(i => i.id === impresaSelId) || gruppo[0];

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Intestazione */}
      <div className="bg-white rounded-xl shadow px-4 sm:px-6 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-xl sm:text-2xl font-bold text-[#1A3A5C]">{impresaSel.ragione_sociale}</h1>
          {nomePratica(impresaSel) && (
            <span className="text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-700">
              {nomePratica(impresaSel)}
            </span>
          )}
        </div>
        {impresaSel.citta && (
          <p className="text-sm text-gray-500 mt-0.5">
            {impresaSel.citta}{impresaSel.provincia ? ` (${impresaSel.provincia})` : ''}
            {impresaSel.partita_iva ? ` — P.IVA ${impresaSel.partita_iva}` : ''}
          </p>
        )}
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-white rounded-xl shadow px-4 py-3 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
              tab === t.key ? 'bg-[#1A3A5C] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: ANAGRAFICA ─────────────────────────────────────────── */}
      {tab === 'anagrafica' && impresaSel && (
        <div className="bg-white rounded-xl shadow p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400">Ragione sociale</p>
              <p className="font-medium text-gray-700">{impresaSel.ragione_sociale || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">P.IVA</p>
              <p className="font-medium text-gray-700">{impresaSel.partita_iva || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Codice fiscale</p>
              <p className="font-medium text-gray-700">{impresaSel.codice_fiscale || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Indirizzo</p>
              <p className="font-medium text-gray-700">{impresaSel.indirizzo || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Città</p>
              <p className="font-medium text-gray-700">
                {[impresaSel.citta, impresaSel.provincia ? `(${impresaSel.provincia})` : null].filter(Boolean).join(' ') || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">CAP</p>
              <p className="font-medium text-gray-700">{impresaSel.cap || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">CCIAA di</p>
              <p className="font-medium text-gray-700">{impresaSel.camera_commercio_di || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">N° iscrizione CCIAA</p>
              <p className="font-medium text-gray-700">{impresaSel.iscrizione_cciaa || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Data iscrizione CCIAA</p>
              <p className="font-medium text-gray-700">
                {impresaSel.data_iscrizione_cciaa ? new Date(impresaSel.data_iscrizione_cciaa).toLocaleDateString('it-IT') : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: LEGALI RAPP. E DIR. TECNICI ────────────────────────── */}
      {tab === 'lrdt' && (
        <SezionePersone impresaId={impresaSel?.id} numeroRichiesta={impresaSel?.numero_richiesta} />
      )}

      {/* ── TAB: DATI ECONOMICI ──────────────────────────────────────── */}
      {tab === 'dati' && (
        <div className="space-y-4">
          {loadingDati ? <Spinner /> : datiEconomici && impresaSel ? (
            <RiepilogoDatiEconomici dati={datiEconomici} impresa={impresaSel} inailMap={inailMap} />
          ) : null}
        </div>
      )}

      {/* ── TAB: LAVORI E CALCOLO SOA ────────────────────────────────── */}
      {tab === 'soa' && (
        <div className="space-y-4">
          {impresaSOA === null ? (
            <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500">
              <p>Nessun certificato SOA collegato a questa azienda.</p>
              <p className="text-xs text-gray-400 mt-1">Contatta l'amministratore per maggiori informazioni.</p>
            </div>
          ) : impresaSOA === undefined ? (
            <Spinner />
          ) : (
            <>
              {/* Info ImpresaSOA */}
              <div className="bg-white rounded-xl shadow px-5 py-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="font-semibold text-[#1A3A5C]">{impresaSOA.ragione_sociale}</p>
                  {impresaSOA.citta && <p className="text-xs text-gray-500">{impresaSOA.citta}</p>}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span>{impresaSOA._count?.lavori || 0} lavori importati</span>
                  {impresaSOA.incremento_icp > 0 && (
                    <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
                      ICP {impresaSOA.incremento_icp}%
                    </span>
                  )}
                </div>
              </div>

              {/* Controlli calcolo */}
              <div className="bg-white rounded-xl shadow px-5 py-4">
                <div className="flex items-end gap-4 flex-wrap">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Periodo lavori</label>
                    <select
                      value={anni}
                      onChange={e => setAnni(parseInt(e.target.value))}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
                    >
                      <option value={15}>Ultimi 15 anni</option>
                      <option value={10}>Ultimi 10 anni</option>
                      <option value={5}>Ultimi 5 anni</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Data di riferimento</label>
                    <input
                      type="date"
                      value={dataRif}
                      onChange={e => setDataRif(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
                    />
                  </div>
                  <button
                    onClick={eseguiCalcolo}
                    disabled={loadingCalcolo}
                    className="bg-[#1A3A5C] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#2E6096] transition disabled:opacity-50"
                  >
                    {loadingCalcolo ? 'Calcolo in corso...' : 'Esegui calcolo'}
                  </button>
                </div>
                {erroreCalcolo && <p className="text-red-600 text-sm mt-2">{erroreCalcolo}</p>}
              </div>

              {loadingCalcolo && <div className="py-4"><Spinner /></div>}

              {calcolo && !loadingCalcolo && (
                <RisultatiCalcolo calcolo={calcolo} anni={anni} nomeImpresa={gruppo[0]?.ragione_sociale} soaId={impresaSOA.id} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Componente: LR e DT in sola lettura ──────────────────────────────
function SezionePersone({ impresaId, numeroRichiesta }) {
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(false);
  const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  useEffect(() => {
    if (!impresaId) return;
    setLoading(true);
    axios.get(`/api/portale/lrdt/${impresaId}`)
      .then(r => {
        if (numeroRichiesta) {
          setDati({
            lr: r.data.lr.filter(p => p.numero_richiesta === numeroRichiesta),
            dt: r.data.dt.filter(p => p.numero_richiesta === numeroRichiesta),
          });
        } else {
          setDati(r.data);
        }
      })
      .finally(() => setLoading(false));
  }, [impresaId, numeroRichiesta]);

  function RigaPersona({ label, val }) {
    if (!val) return null;
    return (
      <div className="flex gap-2 text-sm">
        <span className="text-gray-400 w-36 flex-shrink-0">{label}</span>
        <span className="text-gray-800">{val}</span>
      </div>
    );
  }

  function CardPersona({ p, isDT }) {
    return (
      <div className="border border-gray-200 rounded-lg px-4 py-3 space-y-1">
        <p className="font-semibold text-gray-800 text-sm">
          {p.cognome} {p.nome}{isDT && p.titolo ? ` — ${p.titolo}` : ''}
        </p>
        <RigaPersona label="Nato/a a" val={[p.nato_a, p.provincia_nascita ? `(${p.provincia_nascita})` : null, p.nato_il ? fmtData(p.nato_il) : null].filter(Boolean).join(' ')} />
        <RigaPersona label="Codice fiscale" val={p.codice_fiscale} />
        <RigaPersona label="Residente a" val={p.residente_a ? `${p.residente_a}${p.provincia_residenza ? ` (${p.provincia_residenza})` : ''}` : null} />
        <RigaPersona label="Indirizzo" val={p.indirizzo_residenza} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : !dati ? null : (
        <>
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-3">Legali Rappresentanti</h2>
            {dati.lr.length === 0 ? (
              <p className="text-sm text-gray-400">Nessun legale rappresentante registrato</p>
            ) : (
              <div className="space-y-3">
                {dati.lr.map(p => <CardPersona key={p.id} p={p} isDT={false} />)}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl shadow p-5">
            <h2 className="text-sm font-bold text-[#1A3A5C] uppercase tracking-wide mb-3">Direttori Tecnici</h2>
            {dati.dt.length === 0 ? (
              <p className="text-sm text-gray-400">Nessun direttore tecnico registrato</p>
            ) : (
              <div className="space-y-3">
                {dati.dt.map(p => <CardPersona key={p.id} p={p} isDT={true} />)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Componente: riepilogo dati economici (read-only) ──────────────────
function RiepilogoDatiEconomici({ dati, impresa, inailMap }) {
  const rows = calcolaRiepilogo(dati, inailMap);
  const [attivi, setAttivi] = useState(() => new Set(rows.filter(r => r.hasDati).map(r => r.pos)));

  const sel         = rows.filter(r => attivi.has(r.pos));
  const nSel        = sel.length;
  const totVA       = sel.reduce((s, r) => s + r.va,        0);
  const totVALavori = sel.reduce((s, r) => s + r.vaLavori,  0);
  const totAmmNSLF  = sel.reduce((s, r) => s + r.ammNSLF,   0);
  const totNoliOcc  = sel.reduce((s, r) => s + r.noliOcc,   0);
  const totAmm      = sel.reduce((s, r) => s + r.totAmm,    0);
  const totCP       = sel.reduce((s, r) => s + r.cp,        0);

  const q = totVA > 0 ? totAmm / totVA : null;
  const p = totVA > 0 ? totCP  / totVA : null;
  const qOk = q != null && q >= 0.02;
  const pOk = p != null && p >= 0.15;

  const VA_rid = calcolaVARideterminato(totVA, totAmm, totCP);
  const icpCalc = calcolaICPAuto(totVALavori, totVA, totAmm, totCP, nSel, impresa);
  const icpPct  = icpCalc?.icp || 0;
  const VA_inc  = VA_rid * (1 + icpPct / 100);

  return (
    <div className="space-y-4">
      {/* Sommario */}
      <div className="bg-white rounded-xl shadow p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="font-bold text-[#1A3A5C] text-sm">Riepilogo dati economici</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400">Cifra d'affari</p>
              <p className="font-bold text-lg text-[#1A3A5C]">€ {fmt(totVA)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">V.A. rideterminato</p>
              <p className={`font-bold text-lg ${VA_rid < totVA ? 'text-orange-600' : 'text-[#1A3A5C]'}`}>€ {fmt(VA_rid)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">V.A. incrementato (ICP)</p>
              <p className="font-bold text-lg text-green-700">€ {fmt(VA_inc)}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400">Costo del personale</p>
              <p className="font-semibold">€ {fmt(totCP)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Attrezzature totali</p>
              <p className="font-semibold">€ {fmt(totAmm)}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className={`rounded-lg px-3 py-2 border text-sm ${qOk ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <span className="text-xs text-gray-500 block">q = amm/VA (min 0,02)</span>
              <span className="font-bold">{q != null ? q.toFixed(4) : '—'}</span>
              <span className={`ml-2 text-xs font-bold ${qOk ? 'text-green-600' : 'text-red-600'}`}>{qOk ? '✓ OK' : '✗ NO'}</span>
            </div>
            <div className={`rounded-lg px-3 py-2 border text-sm ${pOk ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <span className="text-xs text-gray-500 block">P = CP/VA (min 15%)</span>
              <span className="font-bold">{p != null ? (p * 100).toFixed(2) + '%' : '—'}</span>
              <span className={`ml-2 text-xs font-bold ${pOk ? 'text-green-600' : 'text-red-600'}`}>{pOk ? '✓ OK' : '✗ NO'}</span>
            </div>
          </div>

          {icpCalc && (
            <div className="text-xs space-y-1">
              {[
                { key: 'A', label: `Patrimonio netto ≥ 5% CA media (soglia: € ${fmt(icpCalc.requisiti.soglia5, 0)})`, val: icpCalc.requisiti.A },
                { key: 'B', label: 'Liquidità verificata (indice ≥ 0,5)', val: icpCalc.requisiti.B },
                { key: 'C', label: 'Reddito positivo in ≥ 2 degli ultimi 3 anni', val: icpCalc.requisiti.C },
                { key: 'D', label: 'Amm. ≥ 2% CA  e  C.P. ≥ 15% CA', val: icpCalc.requisiti.D },
              ].map(r => (
                <div key={r.key} className="flex items-center gap-1.5">
                  <span className={`font-bold w-3 ${r.val ? 'text-green-600' : 'text-red-500'}`}>{r.val ? '✓' : '✗'}</span>
                  <span className={r.val ? 'text-gray-700' : 'text-gray-400'}>{r.key}) {r.label}</span>
                </div>
              ))}
            </div>
          )}

          <div className={`rounded-lg px-3 py-2 border text-sm ${icpPct > 0 ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
            <span className="text-xs text-gray-500 block">ICP calcolato</span>
            <span className={`font-bold text-base ${icpPct > 0 ? 'text-[#1A3A5C]' : 'text-gray-400'}`}>
              {icpPct > 0 ? icpPct + '%' : 'Non spettante'}
            </span>
            {icpCalc?.note && <span className="text-xs text-gray-500 ml-2">({icpCalc.note})</span>}
          </div>
        </div>
      </div>

      {/* Tabella anni */}
      <div className="bg-white rounded-xl shadow p-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
            <tr>
              <th className="px-2 py-2 text-center">Incl.</th>
              <th className="px-2 py-2 text-left">Anno</th>
              <th className="px-2 py-2 text-left">Anno rif.</th>
              <th className="px-3 py-2 text-right">Cifra d'affari €</th>
              <th className="px-3 py-2 text-right">Amm. + NS + L €</th>
              <th className="px-3 py-2 text-right">Noli occ. €</th>
              <th className="px-2 py-2 text-right">% Attrezz.</th>
              <th className="px-3 py-2 text-right">Costo personale €</th>
              <th className="px-2 py-2 text-right">% C.P.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isAttivo = attivi.has(r.pos);
              const pctAmm = r.va > 0 ? r.totAmm / r.va : null;
              const pctCP  = r.va > 0 ? r.cp    / r.va : null;
              return (
                <tr key={r.pos} className={`${!r.hasDati ? 'text-gray-300' : isAttivo ? (i % 2 === 0 ? 'bg-white' : 'bg-gray-50') : 'bg-gray-100 text-gray-400'}`}>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={isAttivo} onChange={() => {
                      setAttivi(prev => { const n = new Set(prev); n.has(r.pos) ? n.delete(r.pos) : n.add(r.pos); return n; });
                    }} disabled={!r.hasDati} className="accent-[#1A3A5C]" />
                  </td>
                  <td className="px-2 py-1.5 font-semibold text-[#1A3A5C]">ANNO{r.pos}</td>
                  <td className="px-2 py-1.5">{r.anno || '—'}</td>
                  <td className="px-3 py-1.5 text-right">{r.va > 0 ? fmt(r.va) : '—'}</td>
                  <td className="px-3 py-1.5 text-right">{r.ammNSLF > 0 ? fmt(r.ammNSLF) : '—'}</td>
                  <td className="px-3 py-1.5 text-right">{r.noliOcc > 0 ? fmt(r.noliOcc) : '—'}</td>
                  <td className={`px-2 py-1.5 text-right font-medium ${pctAmm != null && (pctAmm < 0.02 || pctAmm > 0.03) ? 'text-orange-500' : ''}`}>
                    {pctAmm != null && r.va > 0 ? fmtPct(pctAmm) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right">{r.cp > 0 ? fmt(r.cp) : '—'}</td>
                  <td className={`px-2 py-1.5 text-right font-medium ${pctCP != null && (pctCP < 0.15 || pctCP > 0.225) ? 'text-orange-500' : ''}`}>
                    {pctCP != null && r.va > 0 ? fmtPct(pctCP) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-[#1A3A5C] text-white font-bold">
              <td className="px-2 py-2" />
              <td className="px-2 py-2" colSpan={2}>TOTALI</td>
              <td className="px-3 py-2 text-right">{fmt(totVA)}</td>
              <td className="px-3 py-2 text-right">{fmt(totAmmNSLF)}</td>
              <td className="px-3 py-2 text-right">{fmt(totNoliOcc)}</td>
              <td className="px-2 py-2 text-right">{q != null ? fmtPct(q) : '—'}</td>
              <td className="px-3 py-2 text-right">{fmt(totCP)}</td>
              <td className="px-2 py-2 text-right">{p != null ? fmtPct(p) : '—'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Costanti per calcolo lavori di punta (stesso criterio admin) ──────
const SOGLIE_P = { 'I': 258000, 'II': 516000, 'III': 1033000, 'III bis': 1500000, 'IV': 2582000, 'IV bis': 3500000, 'V': 5165000, 'VI': 10329000, 'VII': 15494000, 'VIII': 20658000 };
const CAT_SPECIALI_P = ['OG2', 'OS2A', 'OS2-A'];

function nPuntaCat(importiOrd, classifica) {
  const soglia = SOGLIE_P[classifica] || 0;
  if (!soglia || !importiOrd.length) return 0;
  if (importiOrd[0] >= soglia * 0.40) return 1;
  if (importiOrd.length >= 2 && importiOrd.slice(0, 2).reduce((a, v) => a + v, 0) >= soglia * 0.55) return 2;
  if (importiOrd.length >= 3 && importiOrd.slice(0, 3).reduce((a, v) => a + v, 0) >= soglia * 0.65) return 3;
  return 0;
}
function nPuntaOG11sub(importiOrd, classifica, subcat) {
  const soglia = SOGLIE_P[classifica] || 0;
  if (!soglia || !importiOrd.length) return 0;
  const [p1, p2, p3] = subcat === 'OS3' ? [0.16, 0.22, 0.26] : [0.28, 0.385, 0.455];
  if (importiOrd[0] >= soglia * p1) return 1;
  if (importiOrd.length >= 2 && importiOrd.slice(0, 2).reduce((a, v) => a + v, 0) >= soglia * p2) return 2;
  if (importiOrd.length >= 3 && importiOrd.slice(0, 3).reduce((a, v) => a + v, 0) >= soglia * p3) return 3;
  return 0;
}

// ── Componente: risultati calcolo SOA ─────────────────────────────────
function RisultatiCalcolo({ calcolo, anni, nomeImpresa, soaId }) {
  const [mostraScaduti, setMostraScaduti] = useState(false);
  const [catIndex, setCatIndex] = useState(0);
  const [scaricandoExcel, setScaricandoExcel] = useState(false);
  // Data di riferimento con cui è stato fatto il calcolo (stessa per schermo, PDF ed Excel)
  const dataRifIso = String(calcolo.data_rif || calcolo.data_report).slice(0, 10);
  const dataRifTesto = new Date(`${dataRifIso}T00:00:00Z`).toLocaleDateString('it-IT', { timeZone: 'UTC' });

  async function scaricaExcel() {
    setScaricandoExcel(true);
    try {
      const r = await axios.get(`/api/portale/soa/${soaId}/lavori-excel?anni=${anni}&data_rif=${dataRifIso}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ElencoLavori_${(nomeImpresa || 'impresa').replace(/[^\w-]+/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('Errore nel download del file Excel');
    } finally {
      setScaricandoExcel(false);
    }
  }
  const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
  const haICP = calcolo.incremento_icp > 0;

  function scaricaPDF() {
    const BLU = [26, 58, 92];
    const LBLU = [238, 244, 251];
    const fmtD = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    let y = 15;

    // Intestazione
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...BLU);
    doc.text(`CALCOLO SOA — ${nomeImpresa || ''}`, 14, y); y += 7;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
    doc.text(
      `Periodo: ultimi ${anni} anni — Data di riferimento: ${dataRifTesto}` +
      `${calcolo.ultimo_indice ? ' — Indice ISTAT: ' + calcolo.ultimo_indice : ''}` +
      `${haICP ? ` — ICP: ${calcolo.incremento_icp}%` : ''}`,
      14, y
    ); y += 8;

    // Tabella classifiche
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...BLU);
    doc.text('Classifiche ottenibili per categoria', 14, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Categoria', 'Totale €', 'Lav. di punta €', 'N. lav.', 'Classifica']],
      body: calcolo.classifiche.map(c => [
        c.categoria,
        fmt(c.totale),
        fmt(c.lavoro_punta),
        c.n_lavori,
        c.classifica ? `Classe ${c.classifica}` : 'Non qualificata',
      ]),
      headStyles: { fillColor: BLU, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: LBLU },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 10;

    // Dettaglio per categoria
    const gruppiPDF = {};
    (calcolo.lavori || []).filter(l => !l.escluso).forEach(lav => {
      const cat = lav.categoria || '—';
      if (!gruppiPDF[cat]) gruppiPDF[cat] = [];
      gruppiPDF[cat].push(lav);
    });
    Object.values(gruppiPDF).forEach(arr => arr.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));

    const head = ['Committente', 'Oggetto', 'Inizio', 'Fine', 'Originale €', 'Rivalutato €', ...(haICP ? ['ICP €'] : []), 'Finale €'];
    const rStyle = { fontStyle: 'bold', fillColor: LBLU, textColor: BLU };

    for (const [cat, lavori] of Object.entries(gruppiPDF).sort(([a], [b]) => a.localeCompare(b))) {
      if (y > 170) { doc.addPage(); y = 14; }
      const infoCat = calcolo.classifiche.find(c => c.categoria === cat);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...BLU);
      doc.text(`${cat}${infoCat?.classifica ? '  —  Classe ' + infoCat.classifica : ''}`, 14, y); y += 4;

      const isOG11 = cat.toUpperCase() === 'OG11';
      if (isOG11) {
        const subGruppi = {};
        lavori.forEach(l => { const s = l.sottocategoria || '—'; if (!subGruppi[s]) subGruppi[s] = []; subGruppi[s].push(l); });
        const rows = [];
        let totOG11 = 0;
        for (const [sub, subLav] of Object.entries(subGruppi).sort(([a], [b]) => a.localeCompare(b))) {
          rows.push([{ content: sub, colSpan: head.length, styles: { fontStyle: 'bold', fillColor: [200, 220, 240], textColor: BLU } }]);
          let totSub = 0;
          subLav.forEach(lav => {
            totSub += lav.importo_finale || 0;
            rows.push([lav.ente_appaltante || '—', lav.oggetto || '—', fmtD(lav.data_inizio), fmtD(lav.data_fine),
              fmt(lav.importo_originale), fmt(lav.importo_rivalutato), ...(haICP ? [fmt(lav.importo_icp)] : []), fmt(lav.importo_finale)]);
          });
          totOG11 += totSub;
          rows.push([{ content: `Totale ${sub}`, colSpan: head.length - 1, styles: rStyle }, { content: fmt(totSub), styles: { ...rStyle, halign: 'right' } }]);
        }
        rows.push([{ content: 'TOTALE OG11', colSpan: head.length - 1, styles: { fontStyle: 'bold', fillColor: BLU, textColor: [255,255,255] } }, { content: fmt(totOG11), styles: { fontStyle: 'bold', fillColor: BLU, textColor: [255,255,255], halign: 'right' } }]);
        autoTable(doc, { startY: y, head: [head], body: rows, headStyles: { fillColor: BLU, textColor: 255, fontSize: 7 }, bodyStyles: { fontSize: 7 }, columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } }, margin: { left: 14, right: 14 } });
      } else {
        let tot = 0;
        const rows = lavori.map(lav => { tot += lav.importo_finale || 0; return [lav.ente_appaltante || '—', lav.oggetto || '—', fmtD(lav.data_inizio), fmtD(lav.data_fine), fmt(lav.importo_originale), fmt(lav.importo_rivalutato), ...(haICP ? [fmt(lav.importo_icp)] : []), fmt(lav.importo_finale)]; });
        rows.push([{ content: `Totale ${cat}`, colSpan: head.length - 1, styles: rStyle }, { content: fmt(tot), styles: { ...rStyle, halign: 'right' } }]);
        autoTable(doc, { startY: y, head: [head], body: rows, headStyles: { fillColor: BLU, textColor: 255, fontSize: 7 }, bodyStyles: { fontSize: 7 }, columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } }, margin: { left: 14, right: 14 } });
      }
      y = doc.lastAutoTable.finalY + 6;
    }

    // Footer: numero pagina + data scaricamento
    const nPagine = doc.getNumberOfPages();
    const pW = doc.internal.pageSize.getWidth();
    const pH = doc.internal.pageSize.getHeight();
    const oggi = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    for (let i = 1; i <= nPagine; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(`Pagina ${i} di ${nPagine}`, pW / 2, pH - 6, { align: 'center' });
      doc.text(`Scaricato il ${oggi}`, pW - 14, pH - 6, { align: 'right' });
    }

    doc.save(`CalcoloSOA_${(nomeImpresa || 'impresa').replace(/\s+/g, '_')}.pdf`);
  }

  // Raggruppa per categoria — solo inclusi; raccoglie tutti gli esclusi separatamente
  const gruppi = {};
  const tuttiScaduti = [];
  (calcolo.lavori || []).forEach(lav => {
    const cat = lav.categoria || '—';
    if (lav.escluso) { tuttiScaduti.push(lav); return; }
    if (!gruppi[cat]) gruppi[cat] = [];
    gruppi[cat].push(lav);
  });
  Object.values(gruppi).forEach(arr => arr.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));
  const classificaMap = {};
  calcolo.classifiche.forEach(c => { classificaMap[c.categoria] = c; });
  const catOrdinate = Object.keys(gruppi).sort((a, b) => a.localeCompare(b));
  const catCorrente = catOrdinate[Math.min(catIndex, catOrdinate.length - 1)];

  const coloreC = c => c ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

  return (
    <div className="space-y-4">

      {/* ── Classifiche ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow p-5 print-area">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-bold text-[#1A3A5C]">Classifiche ottenibili per categoria</h2>
          <div className="flex gap-2">
            <button onClick={scaricaExcel} disabled={scaricandoExcel}
              className="text-sm border border-[#1A3A5C] text-[#1A3A5C] px-3 py-1 rounded-lg hover:bg-[#EEF4FB] transition disabled:opacity-50">
              {scaricandoExcel ? 'Preparo...' : 'Scarica Excel'}
            </button>
            <button onClick={scaricaPDF} className="text-sm border border-[#1A3A5C] text-[#1A3A5C] px-3 py-1 rounded-lg hover:bg-[#EEF4FB] transition">
              Scarica PDF
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          Periodo: ultimi {anni} anni — data di riferimento {dataRifTesto}
          {calcolo.ultimo_indice && ` — Indice ISTAT: ${calcolo.ultimo_indice}`}
          {haICP && ` — ICP: ${calcolo.incremento_icp}%`}
        </p>
        {haICP && (
          <div className="mb-4 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 text-sm">
            <span className="font-bold text-[#1A3A5C]">ICP applicato: {calcolo.incremento_icp}%</span>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
              <tr>
                <th className="text-left px-4 py-2">Categoria</th>
                <th className="text-right px-4 py-2">Totale €</th>
                <th className="text-right px-4 py-2">Lavoro di punta €</th>
                <th className="text-left px-4 py-2">Regola punta</th>
                <th className="text-center px-4 py-2">N. lavori</th>
                <th className="text-center px-4 py-2">Classifica</th>
              </tr>
            </thead>
            <tbody>
              {calcolo.classifiche.map((cat, i) => (
                <tr key={cat.categoria} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-2.5 font-bold text-[#1A3A5C]">{cat.categoria}</td>
                  <td className="px-4 py-2.5 text-right">{fmt(cat.totale)}</td>
                  <td className="px-4 py-2.5 text-right">{fmt(cat.lavoro_punta)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{cat.regola || '—'}</td>
                  <td className="px-4 py-2.5 text-center">{cat.n_lavori}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`px-3 py-0.5 rounded-full text-xs font-bold ${cat.classifica ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {cat.classifica ? `Classifica ${cat.classifica}` : 'Non qualificata'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Navigatore categoria + dettaglio lavori ─────────────────── */}
      {catOrdinate.length > 0 && (
        <div className="bg-white rounded-xl shadow p-5 print-area">

          {/* Barra navigazione */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h3 className="font-bold text-[#1A3A5C] mr-auto">Dettaglio lavori per categoria</h3>
            <span className="text-xs text-gray-400">{catIndex + 1} / {catOrdinate.length}</span>
            <button
              onClick={() => setCatIndex(i => Math.max(0, i - 1))}
              disabled={catIndex === 0}
              className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition"
            >‹</button>
            <select
              value={catCorrente}
              onChange={e => setCatIndex(catOrdinate.indexOf(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            >
              {catOrdinate.map(c => (
                <option key={c} value={c}>{c} ({gruppi[c].length} lav.)</option>
              ))}
            </select>
            <button
              onClick={() => setCatIndex(i => Math.min(catOrdinate.length - 1, i + 1))}
              disabled={catIndex === catOrdinate.length - 1}
              className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition"
            >›</button>
          </div>

          {/* Categoria corrente */}
          {[catCorrente].filter(Boolean).map(cat => {
            const info = classificaMap[cat];
            const lavori = gruppi[cat];
            const isOG11 = cat.toUpperCase() === 'OG11';
            const isSpeciale = !isOG11 && CAT_SPECIALI_P.some(c => cat.toUpperCase().replace(/[\s-]/g, '') === c.replace('-', ''));

            // ── OG11: suddiviso per sottocategoria ──────────────────
            if (isOG11) {
              const subGruppi = {};
              lavori.forEach(lav => {
                const sub = (lav.sottocategoria || '—').toUpperCase();
                if (!subGruppi[sub]) subGruppi[sub] = [];
                subGruppi[sub].push(lav);
              });
              Object.values(subGruppi).forEach(arr => arr.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));
              return (
                <div key={cat} className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-bold text-[#1A3A5C] text-sm">OG11</span>
                    {info && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreC(info.classifica)}`}>{info.classifica ? `Class. ${info.classifica}` : 'Non qualificata'}</span>}
                  </div>
                  {Object.entries(subGruppi).sort(([a], [b]) => a.localeCompare(b)).map(([subcat, subLav]) => {
                    const subcatInfo = info?.sottocategorie?.find(s => s.sottocategoria === subcat);
                    const nPunta = nPuntaOG11sub(subLav.map(l => l.importo_finale || 0), subcatInfo?.classifica, subcat);
                    let prog = 0;
                    return (
                      <div key={subcat} className="mb-4 ml-4 border-l-2 border-[#2E6096] pl-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-[#2E6096] text-sm">{subcat}</span>
                          {subcatInfo && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreC(subcatInfo.classifica)}`}>{subcatInfo.classifica ? `Class. ${subcatInfo.classifica}` : 'Non qualificata'}</span>}
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                              <tr>
                                <th className="text-left px-3 py-1.5">Committente</th>
                                <th className="text-left px-3 py-1.5">Oggetto</th>
                                <th className="text-center px-3 py-1.5">Inizio</th>
                                <th className="text-center px-3 py-1.5">Fine</th>
                                <th className="text-right px-3 py-1.5">Originale €</th>
                                <th className="text-right px-3 py-1.5">Rivalutato €</th>
                                {haICP && <th className="text-right px-3 py-1.5">ICP €</th>}
                                <th className="text-right px-3 py-1.5">Finale €</th>
                                <th className="text-right px-3 py-1.5">Progressivo €</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subLav.map((lav, i) => {
                                prog += lav.importo_finale || 0;
                                const isPunta = i < nPunta;
                                return (
                                  <tr key={lav.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-3 py-1.5 text-gray-600 max-w-[120px] truncate">{lav.ente_appaltante || '—'}</td>
                                    <td className="px-3 py-1.5 max-w-[180px] truncate">{isPunta && <span className="text-yellow-500 mr-1">★</span>}{lav.oggetto || '—'}</td>
                                    <td className="px-3 py-1.5 text-center whitespace-nowrap">{fmtData(lav.data_inizio)}</td>
                                    <td className="px-3 py-1.5 text-center whitespace-nowrap">{fmtData(lav.data_fine)}</td>
                                    <td className="px-3 py-1.5 text-right">{fmt(lav.importo_originale)}</td>
                                    <td className="px-3 py-1.5 text-right">{fmt(lav.importo_rivalutato)}</td>
                                    {haICP && <td className="px-3 py-1.5 text-right text-purple-600">{fmt(lav.importo_icp)}</td>}
                                    <td className="px-3 py-1.5 text-right font-medium">{fmt(lav.importo_finale)}</td>
                                    <td className="px-3 py-1.5 text-right text-[#2E6096] font-semibold">{fmt(prog)}</td>
                                  </tr>
                                );
                              })}
                              <tr className="bg-[#EEF4FB] font-semibold text-[#1A3A5C]">
                                <td className="px-3 py-1.5" colSpan={haICP ? 8 : 7}>Totale {subcat}</td>
                                <td className="px-3 py-1.5 text-right">{fmt(prog)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ── Categoria normale ────────────────────────────────────
            const importiOrd = lavori.map(l => l.importo_finale || 0);
            const nPunta = isSpeciale ? 0 : nPuntaCat(importiOrd, info?.classifica);
            let progressivo = 0;
            return (
              <div key={cat} className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-bold text-[#1A3A5C] text-sm">{cat}</span>
                  {info && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreC(info.classifica)}`}>{info.classifica ? `Class. ${info.classifica}` : 'Non qualificata'}</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                      <tr>
                        <th className="text-left px-3 py-1.5">Committente</th>
                        <th className="text-left px-3 py-1.5">Oggetto</th>
                        <th className="text-center px-3 py-1.5">Inizio</th>
                        <th className="text-center px-3 py-1.5">Fine</th>
                        <th className="text-right px-3 py-1.5">Originale €</th>
                        <th className="text-right px-3 py-1.5">Rivalutato €</th>
                        {haICP && <th className="text-right px-3 py-1.5">ICP €</th>}
                        <th className="text-right px-3 py-1.5">Finale €</th>
                        <th className="text-right px-3 py-1.5">Progressivo €</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lavori.map((lav, i) => {
                        progressivo += lav.importo_finale || 0;
                        const isPunta = !isSpeciale && i < nPunta;
                        return (
                          <tr key={lav.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-1.5 text-gray-600 max-w-[120px] truncate">{lav.ente_appaltante || '—'}</td>
                            <td className="px-3 py-1.5 max-w-[180px] truncate">{isPunta && <span className="text-yellow-500 mr-1">★</span>}{lav.oggetto || '—'}</td>
                            <td className="px-3 py-1.5 text-center whitespace-nowrap">{fmtData(lav.data_inizio)}</td>
                            <td className="px-3 py-1.5 text-center whitespace-nowrap">{fmtData(lav.data_fine)}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(lav.importo_originale)}</td>
                            <td className="px-3 py-1.5 text-right">{fmt(lav.importo_rivalutato)}</td>
                            {haICP && <td className="px-3 py-1.5 text-right text-purple-600">{fmt(lav.importo_icp)}</td>}
                            <td className="px-3 py-1.5 text-right font-medium">{fmt(lav.importo_finale)}</td>
                            <td className="px-3 py-1.5 text-right text-[#2E6096] font-semibold">{fmt(progressivo)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-[#EEF4FB] font-semibold text-[#1A3A5C]">
                        <td className="px-3 py-1.5" colSpan={haICP ? 8 : 7}>Totale {cat}</td>
                        <td className="px-3 py-1.5 text-right">{fmt(progressivo)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {/* Barra navigazione inferiore */}
          {catOrdinate.length > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setCatIndex(i => Math.max(0, i - 1))}
                disabled={catIndex === 0}
                className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition"
              >
                ‹ {catIndex > 0 ? catOrdinate[catIndex - 1] : ''}
              </button>
              <span className="text-xs text-gray-400">{catIndex + 1} / {catOrdinate.length}</span>
              <button
                onClick={() => setCatIndex(i => Math.min(catOrdinate.length - 1, i + 1))}
                disabled={catIndex === catOrdinate.length - 1}
                className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition"
              >
                {catIndex < catOrdinate.length - 1 ? catOrdinate[catIndex + 1] : ''} ›
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Lavori scaduti / esclusi (collassabile, non in stampa) ───── */}
      {tuttiScaduti.length > 0 && (
        <div className="bg-white rounded-xl shadow p-5 print:hidden">
          <button
            onClick={() => setMostraScaduti(v => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#1A3A5C] transition w-full"
          >
            <span className={`transition-transform inline-block ${mostraScaduti ? 'rotate-90' : ''}`}>▶</span>
            Lavori scaduti / esclusi ({tuttiScaduti.length})
          </button>
          {mostraScaduti && (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-gray-500 font-semibold">
                  <tr>
                    <th className="text-left px-3 py-2">Cat.</th>
                    <th className="text-left px-3 py-2">Committente</th>
                    <th className="text-left px-3 py-2">Oggetto</th>
                    <th className="text-center px-3 py-2">Fine</th>
                    <th className="text-right px-3 py-2">Importo €</th>
                    <th className="text-left px-3 py-2">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {tuttiScaduti
                    .sort((a, b) => (a.categoria || '').localeCompare(b.categoria || ''))
                    .map((lav, i) => (
                      <tr key={lav.id} className={`text-gray-400 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-3 py-1.5 whitespace-nowrap font-medium">{lav.categoria || '—'}{lav.sottocategoria ? ` / ${lav.sottocategoria}` : ''}</td>
                        <td className="px-3 py-1.5 max-w-[130px] truncate">{lav.ente_appaltante || '—'}</td>
                        <td className="px-3 py-1.5 max-w-xs truncate">{lav.oggetto || '—'}</td>
                        <td className="px-3 py-1.5 text-center whitespace-nowrap">{lav.data_fine ? new Date(lav.data_fine).toLocaleDateString('it-IT') : '—'}</td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">{fmt(lav.importo_originale)}</td>
                        <td className="px-3 py-1.5 italic">{lav.motivo_esclusione || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
